import React, { useState } from 'react';
import {Link} from "react-router-dom";
import moment from "moment";
import MoneyFormat from "../layout/MoneyFormat";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faEye, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { Button } from 'react-bootstrap';
import {connect} from 'react-redux';
import {setDeleteSaleId, setUpdateSaleId, getSale} from "../../actions/sale";
import { usePermission } from '../../hooks/usePermission';
import { useManagerPermissions } from '../../hooks/useManagerPermissions';
import PropTypes from 'prop-types';

const SaleItem = ({
    sale,
    setDeleteSaleId,
    setUpdateSaleId,
    getSale,
    handleShow
}) => {
    const { userCan } = usePermission();
    const { canManagerEditSale, canManagerDeleteSale } = useManagerPermissions();
    const [showDetails, setShowDetails] = useState(false);

    if (!userCan('view sales')) {
        return null;
    }

    const formatDate = (dateString) => {
        return moment(dateString).format('DD.MM.YYYY HH:mm');
    };

    const getSaleType = (type) => {
        switch(type) {
            case 'product':
                return 'Mahsulot';
            case 'material':
                return 'Material';
            case 'sparepart':
                return 'Ehtiyot qism';
            default:
                return type;
        }
    };

    const setDeleteSale = (id) => {
        setDeleteSaleId(id);
    };

    const setUpdateSale = async (id) => {
        await getSale(id);
        handleShow();
        setUpdateSaleId(id);
    };

    const toggleDetails = () => {
        setShowDetails(!showDetails);
    };

    // Check if the sale is deleted
    const isDeleted = sale.deleted_at !== null && sale.deleted_at !== undefined;

    // Summarize the items for collapsed view
    const itemsCount = sale.items?.length || 0;
    const firstThreeItems = sale.items?.slice(0, 3) || [];
    let itemsSummary = firstThreeItems.map(item => `${parseFloat(item.quantity)} X ${item.sellable?.name}`).join(', ');
    if (itemsCount > 3) {
        itemsSummary += ` va yana ${itemsCount - 3} ta...`;
    }

    return (
        <>
            <tr 
                className={`${showDetails ? 'expanded-row' : ''} ${isDeleted ? 'deleted-item' : ''}`}
                style={isDeleted ? {
                    backgroundColor: '#ffeeee', 
                    border: '2px solid #dc3545',
                    boxShadow: '0 0 5px rgba(220, 53, 69, 0.3)'
                } : {}}
            >
                <td>{sale.id} {isDeleted && <span className="badge bg-danger">O'chirilgan</span>}</td>
                <td>
                    <div className="d-flex align-items-center">
                        <button 
                            className="btn btn-sm btn-outline-secondary me-2" 
                            onClick={toggleDetails}
                        >
                            <FontAwesomeIcon icon={showDetails ? faChevronUp : faChevronDown} />
                        </button>
                        {itemsSummary || "---"}
                    </div>
                </td>
                <td>{sale.seller?.name}</td>
                <td>
                    {userCan('view firms') && sale.firm?.id ? (
                        <Link to={`/firms/${sale.firm?.id}`}>
                            {sale.firm?.name}
                        </Link>
                    ) : (
                        sale.firm?.name || "---"
                    )}
                </td>
                <td>
                    {userCan('view clients') && sale.client?.id ? (
                        <Link to={`/clients/${sale.client?.id}`}>
                            {sale.client?.name}
                        </Link>
                    ) : (
                        sale.client?.name
                    )}
                </td>
                <td><MoneyFormat value={sale.sum}/></td>
                <td>{sale.date}</td>
                <td>{formatDate(sale.created_at)} {isDeleted && (<div className="text-danger small">O'chirilgan: {formatDate(sale.deleted_at)}</div>)}</td>
                <td>{getSaleType(sale.type)}</td>
                <td>{sale.is_special_sale ? 'Ha' : 'Yoq'}</td>
                <td>
                    <div className="d-flex gap-2">
                        {userCan('view sales') && (
                            <Link to={`/sales/${sale.id}`}>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ width: '32px', height: '32px', padding: 0 }}
                                >
                                    <FontAwesomeIcon icon={faEye} />
                                </Button>
                            </Link>
                        )}
                        {canManagerEditSale(sale) && !isDeleted && (
                            <Button
                                variant="outline-primary"
                                size="sm"
                                className="d-flex align-items-center justify-content-center"
                                style={{ width: '32px', height: '32px', padding: 0 }}
                                onClick={() => setUpdateSale(sale.id)}
                            >
                                <FontAwesomeIcon icon={faEdit} />
                            </Button>
                        )}
                        {canManagerDeleteSale(sale) && !isDeleted && (
                            <Button
                                variant="outline-danger"
                                size="sm"
                                className="d-flex align-items-center justify-content-center"
                                style={{ width: '32px', height: '32px', padding: 0 }}
                                onClick={() => setDeleteSale(sale.id)}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </Button>
                        )}
                        {!isDeleted && !sale?.can_edit && (
                            <span className="badge bg-secondary ms-1" title="2 kundan ko'p vaqt o'tganligi sababli o'zgartirib bo'lmaydi">Muddati o'tgan</span>
                        )}
                    </div>
                </td>
            </tr>
            {showDetails && sale.items && sale.items.length > 0 && (
                <tr className={`details-row ${isDeleted ? 'deleted-item' : ''}`}>
                    <td colSpan="11">
                        <div className="details-container">
                            <h6 className="mb-3">Sotuv elementlari ({sale.items.length})</h6>
                            {isDeleted && (
                                <div className="alert alert-danger mb-3">
                                    <small>Bu sotuv {formatDate(sale.deleted_at)} da o'chirilgan</small>
                                </div>
                            )}
                            <table className="table table-sm table-bordered">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Mahsulot/Material</th>
                                        <th>Miqdori</th>
                                        <th>Birlik</th>
                                        <th>Narxi</th>
                                        <th>Jami summa</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sale.items.map((item, index) => (
                                        <tr key={index}>
                                            <td>{index + 1}</td>
                                            <td>{item.sellable?.name}</td>
                                            <td>{parseFloat(item.quantity).toFixed(2)}</td>
                                            <td>{item.unit?.symbol || item.unit?.name || '-'}</td>
                                            <td><MoneyFormat value={item.price}/></td>
                                            <td><MoneyFormat value={item.price * item.quantity}/></td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan="5" className="text-end"><strong>Umumiy summa:</strong></td>
                                        <td><strong><MoneyFormat value={sale.sum}/></strong></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
};

SaleItem.propTypes = {
    sale: PropTypes.shape({
        id: PropTypes.number,
        items: PropTypes.arrayOf(PropTypes.shape({
            quantity: PropTypes.number,
            sellable: PropTypes.shape({
                name: PropTypes.string
            })
        })),
        seller: PropTypes.shape({
            name: PropTypes.string
        }),
        client: PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string
        }),
        sum: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number
        ]),
        date: PropTypes.string,
        created_at: PropTypes.string,
        deleted_at: PropTypes.string,
        type: PropTypes.string,
        is_special_sale: PropTypes.bool
    }).isRequired,
    setDeleteSaleId: PropTypes.func.isRequired,
    setUpdateSaleId: PropTypes.func.isRequired,
    getSale: PropTypes.func.isRequired,
    handleShow: PropTypes.func.isRequired
};

export default connect(null, {setDeleteSaleId, setUpdateSaleId, getSale})(SaleItem);
